import React from 'react';
import { Box, Stack } from '@mui/material';
import { DashboardCard, DashboardCardSkeleton } from '../../../../../shared/components';
import { DemographicsChart, DonutChart, AgeDistributionChart } from '../../../../../shared/components/charts';
import type { DistributionItem } from '../../../../../shared/api/types';
import type { DistributionEntry } from '../api/types';

interface DemographicsSectionProps {
  deptName: string;
  demographics: DistributionItem[] | undefined;
  workingHourPatterns: DistributionEntry[] | undefined;
  ageDistribution: DistributionEntry[] | undefined;
  isLoading: boolean;
}

export const DemographicsSection: React.FC<DemographicsSectionProps> = ({
  deptName,
  demographics,
  workingHourPatterns,
  ageDistribution,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <Stack spacing={2}>
        <DashboardCardSkeleton height={200} />
        <DashboardCardSkeleton height={200} />
        <DashboardCardSkeleton height={200} />
      </Stack>
    );
  }

  const donutData = (workingHourPatterns || []).map((d) => ({
    label: d.name,
    value: d.value,
  }));

  const ageData = (ageDistribution || [])
    .filter((d) => d.name !== 'Other')
    .map((d) => ({
      label: `Age ${d.name}`,
      value: d.value,
    }));

  return (
    <Stack spacing={2} sx={{ flex: 1 }}>
      <DashboardCard
        title='Employee Demographics Overview'
        subtitle={`${deptName} gender breakdown`}
        expandable
        expandedContent={<DemographicsChart data={demographics || []} />}
      >
        <Box sx={{ maxWidth: 500, mx: 'auto', width: '100%' }}>
          <DemographicsChart data={demographics || []} />
        </Box>
      </DashboardCard>
      <DashboardCard
        title='Working Hour Patterns'
        subtitle='Typical work schedule distribution'
        expandable
        expandedContent={<DonutChart data={donutData} height={300} />}
      >
        <DonutChart data={donutData} height={220} />
      </DashboardCard>
      <DashboardCard
        title='Age Distribution'
        subtitle='Organisation demographics'
        expandable
        expandedContent={<AgeDistributionChart data={ageData} height={300} />}
      >
        <AgeDistributionChart data={ageData} height={220} />
      </DashboardCard>
    </Stack>
  );
};
